import "./style.scss";

import { CheckOutlined } from "@mui/icons-material";
import InsertPhoto from "assets/img/svg/insertPhoto";
import LabelComponent from "components/shared/LabelComponent";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import { fileWeightValidator } from "helpers/importData-helpers";
import { useCompanySettings } from "hooks/useCompanySettings";
import type { ChangeEvent } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { sendPicture } from "store/beneficiaryStore";
import { patchCompanyInfo } from "store/settingsStore";
import { useAppDispatch } from "store/store";

import StyledButton from "../StyledButton";
import { StyledMenuItem, StyledSelect } from "../StyledSelect";

const URSSAFCompanySettings = () => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { companyInfos } = useCompanySettings();

    const [isEditing, setIsEditing] = useState(false);
    const [data, setData] = useState<any>();

    useEffect(() => {
        if (companyInfos) setData(companyInfos);
    }, [companyInfos]);

    const handleChange = (ev: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, data: any) => {
        setData((prev: any) => {
            return {
                ...prev,
                [ev.target.name]: ev.target.value,
            };
        });
        setIsEditing(true);
    };

    const handleUpload = (ev: ChangeEvent<HTMLInputElement>) => {
        const sizeError = ev.target.files && fileWeightValidator(ev.target.files[0]);
        if (ev.target.files && ev.target.files.length && !sizeError) {
            dispatch(sendPicture(ev.target.files[0])).then((resp) => {
                if (resp.meta.requestStatus === "fulfilled") {
                    setData((prev: any) => {
                        return {
                            ...prev,
                            avatarUrl: resp.payload,
                        };
                    });
                    setIsEditing(true);
                }
            });
        } else {
            toast.error(`${t("Settings.FileError", { size: "5" })}`);
        }
    };

    const handleSubmit = () => {
        dispatch(patchCompanyInfo(data));
        setIsEditing(false);
    };

    return (
        <div className="urssaf-settings-container">
            <div className="urssaf-settings-info">
                <h2>{t("Settings.Account")}</h2>
                <div className="urssaf-settings-info-bloc">
                    <h3 style={{ marginTop: "2.75rem" }}>{t("Settings.Identity")}</h3>
                    <div>
                        <LabelComponent htmlFor="">{t("NewBeneficiary.Logo")}</LabelComponent>
                        <div className="d-flex align-items-center">
                            <div className="urssaf-settings-account-avatar">
                                {data?.avatarUrl ? <img src={data.avatarUrl} alt="avatar" /> : <InsertPhoto />}
                                <input
                                    type="file"
                                    id="profilPicture"
                                    name="avatarUrl"
                                    style={{ display: "none" }}
                                    onChange={(ev) => handleUpload(ev)}
                                    accept="image/jpeg, image/jpg, image/png, image/gif"
                                />
                            </div>
                            <div style={{ marginLeft: "24px" }}>
                                <label className="urssaf-settings-account-avatar-label" htmlFor="profilPicture">
                                    {t("Settings.UploadPicture")}
                                </label>
                                <p>{t("Settings.FormatPicture")}</p>
                            </div>
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-24 justify-content-between">
                        <div className="flex-grow">
                            <LabelComponent htmlFor="name">{t("CompanySettings.Denomination")}</LabelComponent>
                            <StyledTextField
                                value={data ? data.name : ""}
                                name="name"
                                id="name"
                                onChange={(ev) => handleChange(ev, { ...data })}
                            ></StyledTextField>
                        </div>
                        <div className="flex-grow">
                            <LabelComponent htmlFor="surname">{t("SignUp.CompanyNumber")}</LabelComponent>
                            <StyledTextField
                                value={data ? data.siret : ""}
                                name="siret"
                                id="siret"
                                onChange={(ev) => handleChange(ev, { ...data })}
                            ></StyledTextField>
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-24 justify-content-between">
                        <div className="flex-grow">
                            <LabelComponent htmlFor="activityType">{t("CompanySettings.ActivityType")}</LabelComponent>
                            <StyledTextField
                                value={data ? data.activityType : ""}
                                name="activityType"
                                id="activityType"
                                onChange={(ev) => handleChange(ev, { ...data })}
                            ></StyledTextField>
                        </div>
                        <div className="flex-grow">
                            <LabelComponent htmlFor="distributorType">
                                {t("CompanySettings.DiffusorType")}
                            </LabelComponent>
                            <StyledSelect
                                value={data ? data.distributorType : "1"}
                                name="distributorType"
                                id="distributorType"
                                onChange={(ev: any) => {
                                    handleChange(ev, { ...data });
                                }}
                                sx={{ ".MuiSelect-select": { padding: "14px" } }}
                                fullWidth
                            >
                                <StyledMenuItem value={1}>
                                    {t("CompanySettings.DistributorTypeSelector.DOE")}
                                </StyledMenuItem>
                                <StyledMenuItem value={2}>
                                    {t("CompanySettings.DistributorTypeSelector.DOETHC")}
                                </StyledMenuItem>
                                <StyledMenuItem value={3}>
                                    {t("CompanySettings.DistributorTypeSelector.DCA")}
                                </StyledMenuItem>
                            </StyledSelect>
                        </div>
                    </div>
                </div>
                <div className="urssaf-settings-info-bloc">
                    <h3>{t("Settings.Address")}</h3>
                    <div className="d-flex align-items-center gap-24 justify-content-between">
                        <div className="flex-grow">
                            <LabelComponent htmlFor="address">{t("NewBeneficiary.StreetNumber")}</LabelComponent>
                            <StyledTextField
                                value={data ? data.address : ""}
                                name="address"
                                id="address"
                                onChange={(ev) => handleChange(ev, { ...data })}
                            ></StyledTextField>
                        </div>
                        <div className="flex-grow">
                            <LabelComponent htmlFor="postalCode">{t("NewBeneficiary.ZIPCode")}</LabelComponent>
                            <StyledTextField
                                value={data ? data.postalCode : ""}
                                name="postalCode"
                                id="postalCode"
                                onChange={(ev) => handleChange(ev, { ...data })}
                            ></StyledTextField>
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-24 justify-content-between">
                        <div className="flex-grow">
                            <LabelComponent htmlFor="city">{t("NewBeneficiary.City")}</LabelComponent>
                            <StyledTextField
                                value={data ? data.city : ""}
                                name="city"
                                id="city"
                                onChange={(ev) => handleChange(ev, { ...data })}
                            ></StyledTextField>
                        </div>
                        <div className="flex-grow">
                            <LabelComponent htmlFor="country">{t("NewBeneficiary.Country")}</LabelComponent>
                            <StyledTextField
                                value={data ? data.country : ""}
                                name="country"
                                id="country"
                                onChange={(ev) => handleChange(ev, { ...data })}
                            ></StyledTextField>
                        </div>
                    </div>
                </div>
                <div className="urssaf-settings-info-bloc">
                    <h3>{t("CompanySettings.AdditionalInformations")}</h3>
                    <div className="d-flex align-items-center gap-24 justify-content-between">
                        <div className="flex-grow">
                            <LabelComponent htmlFor="phoneNumber">{t("CompanySettings.PhoneNumber")}</LabelComponent>
                            <StyledTextField
                                value={data ? data.phoneNumber : ""}
                                name="phoneNumber"
                                id="phoneNumber"
                                onChange={(ev) => handleChange(ev, { ...data })}
                            ></StyledTextField>
                        </div>
                        <div className="flex-grow">
                            <LabelComponent htmlFor="apeCode">{t("CompanySettings.APECode")}</LabelComponent>
                            <StyledTextField
                                value={data ? data.apeCode : ""}
                                name="apeCode"
                                id="apeCode"
                                onChange={(ev) => handleChange(ev, { ...data })}
                            ></StyledTextField>
                        </div>
                    </div>
                </div>
                {isEditing && (
                    <div className="urssaf-settings-submit">
                        <StyledButton
                            className="urssaf-settings-submit-btn"
                            onClick={() => {
                                handleSubmit();
                            }}
                        >
                            <CheckOutlined sx={{ width: "18px", marginRight: "10px" }} />
                            {t("Settings.SaveChanges")}
                        </StyledButton>
                    </div>
                )}
            </div>
        </div>
    );
};

export default URSSAFCompanySettings;
